/**
 * Invite code list.
 *
 * Codes are shown by their last four characters only. That is enough to match a
 * row against the mail a user pasted into a support conversation, and it means a
 * screenshot of this page never leaks a usable credential.
 *
 * The collapsed row carries the three states an operator scans for — code state,
 * mail result and where the code came from — next to the suffix and the address.
 * Notes and the two actions that change a code live in the opened part, so a
 * list of twenty codes stays one screen tall.
 */

import { useState } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'

import type { ActivationCodeState } from '@shared/types'

import { ErrorAlert, ErrorBlock, LoadingBlock } from '../../components/layout/states'
import { Button } from '../../components/ui/button'
import { Chip, ChipGroup } from '../../components/ui/chip'
import { Input } from '../../components/ui/input'
import { EmptyState } from '../../components/ui/primitives'
import { useAdminCodes, useResendCode, useRevokeCode } from '../../hooks/use-admin'
import { formatUnixSeconds } from '../../lib/format'
import { currentLocale } from '../../lib/i18n'
import { AdminPage, CollapsibleRow, DetailRow, Pager, StateBadge, adminErrorText, codeStateVariant, mailVariant, usePageParam } from './shared'

const STATES: (ActivationCodeState | 'all')[] = ['unused', 'used', 'expired', 'revoked', 'all']

export function AdminCodesPage() {
  const { t } = useTranslation()
  const { page, params, update, goToPage } = usePageParam()
  const state = params.get('state') ?? 'unused'
  const [email, setEmail] = useState(params.get('email') ?? '')
  const locale = currentLocale()

  const codes = useAdminCodes(state, params.get('email') ?? '', page)
  const resend = useResendCode()
  const revoke = useRevokeCode()

  // Read as text rather than as an `ApiError`: a request that never reached the
  // server still has to say something instead of leaving the list silent.
  const errorText = adminErrorText(resend.error ?? revoke.error, t)

  /** Runs the typed address: the form submit, the Enter key and the button all use it. */
  function runSearch() {
    update({ email, page: '1' })
  }

  const pager = codes.data ? (
    <Pager
      page={codes.data.page}
      pageSize={codes.data.pageSize}
      total={codes.data.total}
      onPage={goToPage}
    />
  ) : null

  return (
    <AdminPage
      title={t('admin.codes.title')}
      description={t('admin.codes.description')}
      current={t('admin.nav.codes')}
      actions={
        <Button asChild size="sm">
          <Link to="/admin/codes/new">{t('admin.codes.issue')}</Link>
        </Button>
      }
    >
      <ChipGroup label={t('admin.codes.filterLabel')}>
        {STATES.map((value) => (
          <Chip key={value} selected={state === value} onClick={() => update({ state: value, page: '1' })}>
            {t(`admin.codeStateFilter.${value}`)}
          </Chip>
        ))}
      </ChipGroup>

      <form
        className="flex flex-col gap-3 sm:flex-row sm:items-center"
        onSubmit={(event) => {
          event.preventDefault()
          runSearch()
        }}
      >
        {/*
          The box carries its name as an accessible label instead of a visible one:
          the only visible「搜索」is the submit button, so clicking the text a
          visitor reads as the search control always runs the search.
        */}
        <Input
          aria-label={t('admin.codes.searchLabel')}
          value={email}
          placeholder={t('admin.codes.searchPlaceholder')}
          onChange={(event) => setEmail(event.target.value)}
          onKeyDown={(event) => {
            // Enter runs the same search the button does, whether or not the
            // browser turns the key press into a form submission.
            if (event.key === 'Enter') {
              event.preventDefault()
              runSearch()
            }
          }}
        />
        <Button type="submit" variant="outline" size="sm" className="shrink-0 self-start">
          {t('admin.codes.search')}
        </Button>
      </form>

      {errorText ? (
        <ErrorAlert message={errorText} />
      ) : null}

      {codes.isPending ? <LoadingBlock label={t('common.loading')} /> : null}
      {codes.isError ? (
        <ErrorBlock
          title={t('admin.common.error')}
          retryLabel={t('admin.common.retry')}
          onRetry={() => void codes.refetch()}
        />
      ) : null}
      {codes.data && codes.data.items.length === 0 ? (
        <EmptyState title={t('admin.codes.empty')} />
      ) : null}

      {pager}

      <div className="flex flex-col gap-2">
        {codes.data?.items.map((code) => (
          <CollapsibleRow
            key={code.id}
            title={<span className="font-mono text-sm">••••{code.codeSuffix}</span>}
            meta={
              <span className="text-muted-foreground truncate text-xs">
                {code.email} ·{' '}
                {t('admin.codes.createdAt', { time: formatUnixSeconds(code.createdAt, locale) })}
              </span>
            }
            badges={
              <>
                <StateBadge
                  label={t(`admin.codeState.${code.state}`)}
                  variant={codeStateVariant(code.state)}
                />
                <StateBadge
                  label={t(`admin.mailStatus.${code.mailStatus}`)}
                  variant={mailVariant(code.mailStatus)}
                />
                <StateBadge
                  label={
                    code.applicationId === null
                      ? t('admin.codes.sourceDirect')
                      : t('admin.codes.sourceApplication')
                  }
                  variant="outline"
                />
              </>
            }
          >
            <DetailRow label={t('admin.codes.email')}>{code.email}</DetailRow>
            <DetailRow label={t('admin.codes.expiresAtLabel')}>
              {formatUnixSeconds(code.expiresAt, locale)}
            </DetailRow>
            {code.note ? <DetailRow label={t('admin.codes.note')}>{code.note}</DetailRow> : null}
            {code.state === 'unused' ? (
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={resend.isPending}
                  onClick={() => resend.mutate(code.id)}
                >
                  {t('admin.codes.resend')}
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  disabled={revoke.isPending}
                  onClick={() => revoke.mutate(code.id)}
                >
                  {t('admin.codes.revoke')}
                </Button>
              </div>
            ) : null}
          </CollapsibleRow>
        ))}
      </div>

      {pager}
    </AdminPage>
  )
}
